// Video Editor — local, ephemeral types.
//
// Everything here lives only in the browser tab's memory. No clip, no
// rendered output, and no metadata is ever sent to the BFF or persisted.
// Object URLs are minted on add and revoked on remove/reset.

export type ClipId = string;

export interface EditorClip {
  /** Stable local id (render key + ffmpeg virtual filename seed). */
  id: ClipId;
  /** The raw user-dropped file (kept in memory only). */
  file: File;
  /** Object URL for in-browser preview (revoked on remove). */
  url: string;
  /** Original filename for display. */
  name: string;
  /** Bytes. */
  size: number;
  /** Probed duration in seconds (best-effort via <video> metadata). */
  durationSec: number | null;
  /** Probed natural pixel size (best-effort). */
  width: number | null;
  height: number | null;
}

/** Lifecycle of the ffmpeg.wasm engine. */
export type EngineState = 'idle' | 'loading' | 'ready' | 'error';

/** Lifecycle of an export (splice) job. */
export type ExportPhase =
  | 'idle'
  | 'preparing'   // writing inputs into the virtual FS
  | 'encoding'    // ffmpeg running
  | 'finalizing'  // reading output + minting blob
  | 'done'
  | 'error';

export interface ExportProgress {
  phase: ExportPhase;
  /** 0..1 overall, best-effort. */
  ratio: number;
  /** Human label, e.g. "Encoding clip 2 of 3". */
  label: string;
}

export interface ExportResult {
  /** Object URL for the spliced MP4 (revoked on reset / re-export). */
  url: string;
  /** Bytes of the rendered file. */
  size: number;
  /** Suggested download filename. */
  filename: string;
  /** Total duration in seconds (sum of inputs), best-effort. */
  durationSec: number | null;
}

/** Output canvas presets the user can pick for normalization. */
export interface ResolutionPreset {
  id: string;
  label: string;
  width: number;
  height: number;
  fps: number;
}

export const RESOLUTION_PRESETS: ResolutionPreset[] = [
  { id: '720p30', label: '720p · 30fps', width: 1280, height: 720, fps: 30 },
  { id: '1080p30', label: '1080p · 30fps', width: 1920, height: 1080, fps: 30 },
  { id: '480p30', label: '480p · 30fps', width: 854, height: 480, fps: 30 },
  { id: 'vertical1080', label: 'Vertical 1080×1920 · 30fps', width: 1080, height: 1920, fps: 30 },
];
